import React, { PropsWithChildren, useContext, useMemo } from "react";

/** Returns the busyBlocker you can use and triggers a rerender when busy */
// Remark: Can we separate state and busyBlocker object to prevent updates where only object is set.
const useBusyBlocker = () => {
	// Instead of using useState, make it possible to subscribe to the change
	// This prevents a rerender when just using the busyBlocker to mark something as busy
	const busyEmitter = useMemo(() => eXpress.core.createEventEmitter<boolean>(), []);

	const busyBlocker = useMemo(() => {
		// (maybe we need to move to a new React Context because it will also contain UI)
		// const keyboard = new eXpress.creator.Keyboard(log);
		// const keyboardBlocker = new eXpress.creator.KeyboardBlocker(keyboard);
		const busyBlockerUi = new eXpress.BalancedScope(
			() => {
				busyEmitter.emit(true);
			},
			() => {
				busyEmitter.emit(false);
			},
		);
		return eXpress.BalancedScope.combine(busyBlockerUi);
	}, [busyEmitter]);

	// return a 'stable' object because it is used in a ContextProvider
	// we return the same object when there are no changes, else
	// - all children of the BusyBlockerContextProvider will rerender
	// - all components that use useBusyBlockerContext will rerender
	return useMemo(
		() => ({
			busyEmitter: busyEmitter as Pick<typeof busyEmitter, "subscribe">,
			busyBlocker,
		}),
		[busyEmitter, busyBlocker],
	);
};

const BusyBlockerContext = React.createContext({} as ReturnType<typeof useBusyBlocker>);

export const BusyBlockerContextProvider = ({ children }: PropsWithChildren) => {
	const busyBlockerContext = useBusyBlocker();

	// Make busyBlockerContext available on console in 'Debug' mode
	// Assuming only one context is used, else global variable overridden
	if (eXpress.isDebugMode) {
		// Allow, this is only used for debugging
		// eslint-disable-next-line react-compiler/react-compiler
		(window as any)._busyBlockerContext = busyBlockerContext;
	}

	return (
		<BusyBlockerContext.Provider value={busyBlockerContext}>
			{children}
		</BusyBlockerContext.Provider>
	);
};
BusyBlockerContextProvider.displayName = "BusyBlockerContextProvider";

export const useBusyBlockerContext = () => useContext(BusyBlockerContext);
